import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportDataTable, {
  ReportDataTableColumn,
} from '@components/pages/Reports/shared/ReportDataTable.component'
import ReportFullViewTableTitle, {
  useReportFullViewSection,
} from '@components/pages/Reports/shared/ReportFullViewTableTitle.component'
import ReportExpandableSection from '@components/pages/Reports/shared/ReportExpandableSection.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  BirthdayMilestoneRow,
  FAMILY_OPPORTUNITY_FILTERS,
  FAMILY_OPPORTUNITY_KPI_DEFINITIONS,
  HouseholdSchedulingGapRow,
} from '@constants/Reports/familyOpportunityIntelligence.constants'
import {
  ReportsDistributionView,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { useFamilyOpportunityIntelligenceReport } from '@hooks/reports/useFamilyOpportunityIntelligenceReport'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import { formatReportKpiFooter } from '@utils/reports/reportKpiFooter.util'
import {
  computeDynamicCountAxis,
  computePercentAxis,
} from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_YAXIS_WIDTH_WIDE = 46

const FAMILY_BAR_CHART_MARGIN = REPORT_BAR_CHART_MARGIN
const FAMILY_BAR_Y_AXIS_WIDTH = REPORT_YAXIS_WIDTH_WIDE

function formatKpiCount(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—'
  return Math.round(value).toLocaleString('en-US')
}

function FamilyOpportunityIntelligenceReport(): React.ReactElement {
  const [sameFamilyView, setSameFamilyView] =
    useState<ReportsDistributionView>('Count')
  const [multiFamilyView, setMultiFamilyView] =
    useState<ReportsDistributionView>('Count')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const birthdayFullView = useReportFullViewSection('foi-birthday')
  const householdGapsFullView = useReportFullViewSection('foi-gaps')

  const { loading, error, data } = useFamilyOpportunityIntelligenceReport(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage:
      'Failed to load family opportunity intelligence data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return FAMILY_OPPORTUNITY_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footerDescriptor ?? def.footer ?? '—',
        unitInline: def.unitInline,
        value: loading ? '…' : '—',
        tone: 'neutral',
      }))
    }

    const valueById: Record<string, number> = {
      'same-family': data.sameFamilyOpportunities.value,
      'household-gaps': data.householdsWithGaps.value,
      birthdays: data.upcomingBirthdays.value,
      'multi-family-gaps': data.multiFamilySchedulingGaps.value,
    }

    const priorById: Record<string, number> = {
      'same-family': data.sameFamilyOpportunitiesFooter.value,
      'household-gaps': data.householdsWithGapsFooter.value,
      birthdays: data.upcomingBirthdaysFooter.value,
      'multi-family-gaps': data.multiFamilySchedulingGapsFooter.value,
    }

    return FAMILY_OPPORTUNITY_KPI_DEFINITIONS.map((def) => {
      const footerResult = formatReportKpiFooter(
        def.footerMode ?? 'percentChange',
        valueById[def.id],
        priorById[def.id],
        def.footerDescriptor,
      )

      return {
        id: def.id,
        title: def.title,
        footer: footerResult.text,
        tone: footerResult.tone,
        unitInline: def.unitInline,
        value: formatKpiCount(valueById[def.id]),
      }
    })
  }, [data, loading])

  const sameFamilyChartData = useMemo(() => {
    const buckets = data?.sameFamilyByCategory.buckets ?? []
    return buckets.map((row) => ({
      ...row,
      barValue: sameFamilyView === 'Count' ? row.count : row.percentage,
      topLabel:
        sameFamilyView === 'Count' ? String(row.count) : `${row.percentage}%`,
    }))
  }, [data, sameFamilyView])

  const sameFamilyYAxis = useMemo(() => {
    const values = sameFamilyChartData.map((row) => row.barValue)
    return sameFamilyView === 'Count'
      ? computeDynamicCountAxis(values)
      : computePercentAxis(values)
  }, [sameFamilyChartData, sameFamilyView])

  const multiFamilyChartData = useMemo(() => {
    const buckets = data?.multiFamilyGapBySpread.buckets ?? []
    return buckets.map((row) => ({
      ...row,
      barValue: multiFamilyView === 'Count' ? row.count : row.percentage,
      topLabel:
        multiFamilyView === 'Count' ? String(row.count) : `${row.percentage}%`,
    }))
  }, [data, multiFamilyView])

  const multiFamilyYAxis = useMemo(() => {
    const values = multiFamilyChartData.map((row) => row.barValue)
    return multiFamilyView === 'Count'
      ? computeDynamicCountAxis(values)
      : computePercentAxis(values)
  }, [multiFamilyChartData, multiFamilyView])

  const birthdayRows = useMemo((): BirthdayMilestoneRow[] => {
    if (!data) return []
    return data.birthdayMilestones.rows.map((row) => ({
      id: row.id,
      patientName: row.patientName,
      date: row.date,
      household: row.household,
    }))
  }, [data])

  const householdGapRows = useMemo((): HouseholdSchedulingGapRow[] => {
    if (!data) return []
    return data.householdSchedulingGaps.rows.map((row) => ({
      id: row.id,
      household: row.household,
      members: row.members,
      dueUnscheduled: row.dueUnscheduled,
    }))
  }, [data])

  const birthdayColumns: ReportDataTableColumn<BirthdayMilestoneRow>[] =
    useMemo(
      () => [
        { key: 'patientName', label: 'Patient Name' },
        { key: 'date', label: 'Date' },
        { key: 'household', label: 'Household' },
      ],
      [],
    )

  const householdGapColumns: ReportDataTableColumn<HouseholdSchedulingGapRow>[] =
    useMemo(
      () => [
        { key: 'household', label: 'Household' },
        {
          key: 'members',
          label: 'Members',
          headerClassName: 'reports-patient-value-revenue__table-head--num',
          cellClassName: 'reports-patient-value-revenue__table-cell--num',
        },
        {
          key: 'dueUnscheduled',
          label: 'Due/Unscheduled',
          headerClassName: 'reports-patient-value-revenue__table-head--num',
          cellClassName: 'reports-patient-value-revenue__table-cell--num',
        },
      ],
      [],
    )

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.sameFamilyOpportunities.value,
      data?.householdsWithGaps.value,
      data?.upcomingBirthdays.value,
      data?.multiFamilySchedulingGaps.value,
    ),
  )
  const sameFamilyEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.sameFamilyByCategory?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.count, row.percentage),
    ),
  )
  const birthdayEmpty = shouldShowDashboardSectionEmpty(
    loading,
    birthdayRows.length > 0,
  )
  const multiFamilyEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.multiFamilyGapBySpread?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.count, row.percentage),
    ),
  )
  const householdGapsEmpty = shouldShowDashboardSectionEmpty(
    loading,
    householdGapRows.length > 0,
  )

  return (
    <div className="reports-patient-base-health reports-family-opportunity">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Family &amp; Opportunity Intelligence">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={FAMILY_OPPORTUNITY_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      <div className="reports-family-opportunity__stack">
        <ReportExpandableSection sectionId="foi-kpi">
          <div className="reports-family-opportunity__kpi">
            {kpiEmpty ? (
              <DashboardSectionEmptyState />
            ) : (
              <ReportKpiGrid items={kpiItems} />
            )}
          </div>
        </ReportExpandableSection>
        <div className="reports-family-opportunity__grid">
          <ReportExpandableSection sectionId="foi-top">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Same-Family Opportunities
                  </ReportSectionHeading>
                  {sameFamilyEmpty ? null : (
                    <ReportViewTabs
                      options={REPORTS_DISTRIBUTION_VIEW_TABS}
                      value={sameFamilyView}
                      onChange={(value) =>
                        setSameFamilyView(value as ReportsDistributionView)
                      }
                      className="reports-patient-base-health__view-group"
                    />
                  )}
                </div>
                <p className="reports-family-opportunity__chart-note">
                  Count of patients with household-linked opportunities
                </p>
                {sameFamilyEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-family-opportunity__chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={sameFamilyChartData}
                        margin={FAMILY_BAR_CHART_MARGIN}
                        barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                      >
                        <CartesianGrid
                          vertical={false}
                          strokeDasharray="0"
                          stroke={REPORT_CHART_GRID_STROKE}
                        />
                        <XAxis
                          dataKey="label"
                          axisLine={false}
                          tickLine={false}
                          interval={0}
                          tick={REPORT_CHART_AXIS_TICK}
                          height={44}
                        />
                        <YAxis
                          domain={sameFamilyYAxis.domain}
                          ticks={sameFamilyYAxis.ticks}
                          tickFormatter={sameFamilyYAxis.tickFormatter}
                          allowDecimals={sameFamilyView === 'Percentage'}
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                          width={FAMILY_BAR_Y_AXIS_WIDTH}
                          tickMargin={2}
                        />
                        <Tooltip
                          formatter={(value: number) =>
                            sameFamilyView === 'Count' ? value : `${value}%`
                          }
                          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                        />
                        <Bar
                          dataKey="barValue"
                          fill="var(--blue-400)"
                          radius={[8, 8, 8, 8]}
                          barSize={100}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>

          <ReportExpandableSection sectionId="foi-birthday">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column reports-patient-value-revenue__column--fill">
                <div className="reports-patient-base-health__chart-head">
                  <ReportFullViewTableTitle
                    sectionId="foi-birthday"
                    title="Birthdays &amp; Milestones (Next 30 Days)"
                    disableEnter={birthdayEmpty}
                  />
                </div>
                <p className="reports-family-opportunity__chart-note">
                  Top outreach list (front desk/marketing)
                </p>
                {birthdayEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-family-opportunity__table-block">
                    <ReportDataTable
                      key={
                        birthdayFullView.isFullView
                          ? 'foi-birthday-fv'
                          : 'foi-birthday-card'
                      }
                      columns={birthdayColumns}
                      rows={birthdayRows}
                      rowKey={(row) => row.id}
                      initialPageSize={5}
                      className={[
                        'reports-patient-value-revenue__provider-table',
                        'reports-family-opportunity__birthday-table',
                        birthdayFullView.isFullView
                          ? 'reports-patient-value-revenue__provider-table--full-view'
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      fullViewSectionId="foi-birthday"
                      fullViewSearchWithFilter
                    />
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>
        </div>

        <div className="reports-family-opportunity__grid">
          <ReportExpandableSection sectionId="foi-multi">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Multi-family Scheduling Gap
                  </ReportSectionHeading>
                  {multiFamilyEmpty ? null : (
                    <ReportViewTabs
                      options={REPORTS_DISTRIBUTION_VIEW_TABS}
                      value={multiFamilyView}
                      onChange={(value) =>
                        setMultiFamilyView(value as ReportsDistributionView)
                      }
                      className="reports-patient-base-health__view-group"
                    />
                  )}
                </div>
                <p className="reports-family-opportunity__chart-note">
                  Households with appointments spread across multiple dates
                </p>
                {multiFamilyEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-family-opportunity__chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={multiFamilyChartData}
                        margin={FAMILY_BAR_CHART_MARGIN}
                        barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                      >
                        <CartesianGrid
                          vertical={false}
                          strokeDasharray="0"
                          stroke={REPORT_CHART_GRID_STROKE}
                        />
                        <XAxis
                          dataKey="label"
                          axisLine={false}
                          tickLine={false}
                          interval={0}
                          tick={REPORT_CHART_AXIS_TICK}
                          height={30}
                        />
                        <YAxis
                          domain={multiFamilyYAxis.domain}
                          ticks={multiFamilyYAxis.ticks}
                          tickFormatter={multiFamilyYAxis.tickFormatter}
                          allowDecimals={multiFamilyView === 'Percentage'}
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                          width={FAMILY_BAR_Y_AXIS_WIDTH}
                          tickMargin={2}
                        />
                        <Tooltip
                          formatter={(value: number) =>
                            multiFamilyView === 'Count' ? value : `${value}%`
                          }
                          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                        />
                        <Bar
                          dataKey="barValue"
                          fill="var(--blue-400)"
                          radius={[8, 8, 8, 8]}
                          barSize={100}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>

          <ReportExpandableSection sectionId="foi-gaps">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column reports-patient-value-revenue__column--fill">
                <div className="reports-patient-base-health__chart-head">
                  <ReportFullViewTableTitle
                    sectionId="foi-gaps"
                    title="Household Scheduling Gaps"
                    disableEnter={householdGapsEmpty}
                  />
                </div>
                <p className="reports-family-opportunity__chart-note">
                  Families with due members not scheduled
                </p>
                {householdGapsEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-family-opportunity__table-block">
                    <ReportDataTable
                      key={
                        householdGapsFullView.isFullView
                          ? 'foi-gaps-fv'
                          : 'foi-gaps-card'
                      }
                      columns={householdGapColumns}
                      rows={householdGapRows}
                      rowKey={(row) => row.id}
                      initialPageSize={5}
                      className={[
                        'reports-patient-value-revenue__provider-table',
                        'reports-family-opportunity__household-gaps-table',
                        householdGapsFullView.isFullView
                          ? 'reports-patient-value-revenue__provider-table--full-view'
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      fullViewSectionId="foi-gaps"
                      fullViewSearchWithFilter
                    />
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>
        </div>
      </div>
    </div>
  )
}

export default FamilyOpportunityIntelligenceReport
